import './AffirmationBuilder.scss'

import { useState } from 'react'
import {
  Button,
  ButtonGroup,
  Container,
  Row,
  Col,
  Form,
  FloatingLabel
} from 'react-bootstrap'

import { REMBLE_API_URL, buildRequestURL } from '../../../common/api'
import {
  getAffirmationPractices,
  getAffirmationData,
  getAffirmationAssets
} from '../../../common/api/Practices'
import { useEffect } from 'react'
import { useCallback } from 'react'

const presets = [
  {
    name: 'Default',
    background: 'None',
    watermark: 'lockup-green',
    background_color: '#ffffff',
    background_gradient: '#ffffff',
    text_color: '#2e933c',
    text_shadow_size: 0,
    text_alignment: 'center'
  },
  {
    name: 'Green',
    background: 'None',
    watermark: 'lockup-white',
    background_color: '#2e933c',
    background_gradient: '#2e933c',
    text_color: '#ffffff',
    text_shadow_size: 10,
    text_alignment: 'center'
  },
  {
    name: 'Black',
    background: 'None',
    watermark: 'wordmark-white',
    background_color: '#000000',
    background_gradient: '#000000',
    text_color: '#ffffff',
    text_shadow_size: 0,
    text_alignment: 'center'
  },
  {
    name: `BlueSteel`,
    background: 'None',
    watermark: 'wordmark-white',
    background_color: '#6197d7',
    background_gradient: '#365072',
    text_color: '#ffffff',
    text_shadow_size: 10,
    text_alignment: 'center'
  },
  {
    name: 'Tangerine',
    background: 'None',
    watermark: 'wordmark-white',
    background_color: '#ff956e',
    background_gradient: '#e53d00',
    text_color: '#ffffff',
    text_shadow_size: 10,
    text_alignment: 'center'
  },
  {
    name: 'Bubbles',
    background: 'waimakariri',
    watermark: 'wordmark-black',
    background_color: '#ffffff',
    background_gradient: '#ffffff',
    text_color: '#000000',
    text_shadow_size: 0,
    text_alignment: 'center'
  }
]

const defaultPreset = presets[0]

/**
 * Simple AffirmationBuilder component. Used for testing the affirmation generator
 */
function AffirmationBuilder() {
  const baseURL = `${REMBLE_API_URL}/practices/affirmations/create`
  const [category, setCategory] = useState()
  const [categories, setCategories] = useState([])
  const [affirmations, setAffirmations] = useState([])
  const [watermarks, setWatermarks] = useState([])
  const [backgrounds, setBackgrounds] = useState([])

  const [text, setText] = useState('Amazing')
  const [textColor, setTextColor] = useState(defaultPreset.text_color)
  const [textAlignment, setTextAlignment] = useState(
    defaultPreset.text_alignment
  )
  const [textShadow, setTextShadow] = useState(defaultPreset.text_shadow_size)
  // const [textShadowStrength, setTextShadowStrength] = useState(100)
  const [fontStyleBold, setFontStyleBold] = useState(!!defaultPreset.bold)
  const [fontStyleItalic, setFontStyleItalic] = useState(!!defaultPreset.italic)
  const [background, setBackground] = useState(defaultPreset.backgound)
  const [backgroundColor, setBackgroundColor] = useState(
    defaultPreset.background_color
  )
  const [backgroundGradient, setBackgroundGradient] = useState(
    defaultPreset.background_gradient || defaultPreset.background_color
  )
  const [watermark, setWatermark] = useState(defaultPreset.wordmark)
  const [url, setUrl] = useState()

  const getUrlForProps = useCallback(
    props => {
      // Make sure we have text
      if (!props['text']) props['text'] = props['name']

      return encodeURI(buildRequestURL(baseURL, props))
    },
    [baseURL]
  )

  const setPreset = preset => {
    if (preset.text_alignment) setTextAlignment(preset.text_alignment)
    if (preset.bold) setFontStyleBold(true)
    if (preset.italic) setFontStyleItalic(true)
    if (preset.text_color) setTextColor(preset.text_color)
    if (preset.background) setBackground(preset.background)
    if (preset.background_color) setBackgroundColor(preset.background_color)
    if (preset.watermark) setWatermark(preset.watermark)
    if (preset.background_gradient)
      setBackgroundGradient(preset.background_gradient)

    // Allow "0" values
    setTextShadow(preset.text_shadow_size || 0)
  }

  const updateFontStyle = style => {
    // Toggle styles
    if (style === 'bold') setFontStyleBold(!fontStyleBold)
    if (style === 'italic') setFontStyleItalic(!fontStyleItalic)
  }

  const buildPreviewUrl = useCallback(() => {
    let values = {
      text,
      text_color: textColor,
      text_alignment: textAlignment,
      text_shadow_size: textShadow,
      text_shadow_strength: textShadow * 15,
      font_style: [fontStyleBold ? 'bold' : '', fontStyleItalic ? 'italic' : '']
        .filter(x => x)
        .join('-'),
      background,
      background_color: backgroundColor,
      background_gradient: backgroundGradient,
      watermark
    }

    let previewUrl = getUrlForProps(values)

    // Don't update if it hasn't changed
    if (previewUrl !== url) setUrl(previewUrl)

    return previewUrl
  }, [
    text,
    background,
    watermark,
    backgroundColor,
    backgroundGradient,
    textColor,
    textShadow,
    textAlignment,
    fontStyleBold,
    fontStyleItalic,
    url,
    getUrlForProps
  ])

  useEffect(() => {
    buildPreviewUrl()
  }, [background, watermark, textColor, backgroundColor, buildPreviewUrl])

  useEffect(() => {
    // Return early if we've already collected the image assets
    if (watermarks.length || backgrounds.length) return
    ;(async function getAssetsAsync() {
      const bgs = await getAffirmationAssets('backgrounds')
      const marks = await getAffirmationAssets('watermarks')

      setBackgrounds(bgs.sort())
      setWatermarks(marks.sort())
    })()
  }, [watermarks.length, backgrounds.length])

  useEffect(() => {
    // Return early if we don't have a category selected
    if (!category) return
    ;(async function getAffirmationDataAsync() {
      const result = await getAffirmationData(category)
      setAffirmations(result)
    })()
  }, [category])

  useEffect(() => {
    // Return early if we already have the data
    if (categories.length) return
    ;(async function getAffirmationDataAsync() {
      const result = await getAffirmationPractices()
      setCategories(result)
    })()
    buildPreviewUrl()
  }, [categories.length, buildPreviewUrl])

  return (
    <Container className='affirmation-container'>
      <h1 className='mt-3'>Affirmation Builder</h1>
      <Row xs={1} md={2}>
        <Col>
          <h6>Options</h6>
          <Form
            onSubmit={e => {
              e.preventDefault()
              return false
            }}
          >
            <strong>Affirmation Text</strong>
            <div className='category-wrapper'>
              <small>Use existing affirmation:</small>
              <Form.Group className='my-3' controlId='formCategories'>
                <FloatingLabel
                  controlId='floatingCategories'
                  label='Affirmation Categories'
                >
                  <Form.Select
                    name='category'
                    onChange={e => setCategory(e.currentTarget.value)}
                    value={background}
                  >
                    <option value=''>Select an affirmation category</option>
                    {categories.map(option => (
                      <option key={option.term_id} value={option.term_id}>
                        {option.name}
                      </option>
                    ))}
                  </Form.Select>
                </FloatingLabel>
              </Form.Group>
              <Form.Group
                hidden={!category || !affirmations.length}
                className='my-3'
                controlId='formAffirmations'
              >
                <FloatingLabel
                  controlId='floatingAffirmations'
                  label='Affirmations'
                >
                  <Form.Select
                    name='affirmation'
                    onChange={e => setText(e.currentTarget.value)}
                    value={text}
                  >
                    <option value=''>Select an affirmation</option>
                    {affirmations.length > 0 &&
                      affirmations.map(option => (
                        <option key={option.ID} value={option.post_title}>
                          {option.post_title}
                        </option>
                      ))}
                  </Form.Select>
                </FloatingLabel>
              </Form.Group>
            </div>
            <Form.Group className='my-3' controlId='formText'>
              <FloatingLabel controlId='floatingText' label='Custom text'>
                <Form.Control
                  name='text'
                  value={text}
                  size='lg'
                  placeholder='Custom text'
                  required
                  autoFocus={true}
                  autoCapitalize='off'
                  type='text'
                  onChange={e => setText(e.currentTarget.value)}
                />
              </FloatingLabel>
            </Form.Group>
            <hr />
            <strong>Presets</strong>
            <Row md={3} lg={6} className='mb-3'>
              {presets.map(preset => (
                <Col
                  key={preset.name}
                  className='preset'
                  onClick={_ => setPreset(preset)}
                >
                  <small className='title'>{preset.name}</small>
                  <br />
                  <img src={getUrlForProps(preset)} alt={preset.name} />
                </Col>
              ))}
            </Row>
            <hr />

            <strong>Text Options</strong>
            <Row xs={3}>
              <Col>
                <Form.Group className='my-3' controlId='formTextColor'>
                  <small>
                    <label>Color</label>
                  </small>
                  <Form.Control
                    name='textColor'
                    value={textColor}
                    type='color'
                    onChange={e => setTextColor(e.currentTarget.value)}
                  />
                </Form.Group>
              </Col>
              <Col>
                <Form.Group className='my-3' controlId='formFontStyle'>
                  <small>
                    <label>Style</label>
                  </small>
                  <br />
                  <ButtonGroup aria-label='Text Style'>
                    <Button
                      variant='secondary'
                      onClick={e => {
                        updateFontStyle('bold')
                      }}
                      active={fontStyleBold}
                    >
                      <i className='icon bi-type-bold'></i>
                    </Button>
                    <Button
                      variant='secondary'
                      onClick={e => {
                        updateFontStyle('italic')
                      }}
                      active={fontStyleItalic}
                    >
                      <i className='icon bi-type-italic'></i>
                    </Button>
                  </ButtonGroup>
                </Form.Group>
              </Col>
              <Col>
                <Form.Group className='my-3' controlId='formTextAlignment'>
                  <small>
                    <label>Alignment</label>
                  </small>
                  <br />
                  <ButtonGroup aria-label='Text Alignment'>
                    <Button
                      variant='secondary'
                      onClick={e => {
                        setTextAlignment('left')
                      }}
                      active={textAlignment.indexOf('left') > -1}
                    >
                      <i className='icon bi-text-left'></i>
                    </Button>
                    <Button
                      variant='secondary'
                      onClick={e => {
                        setTextAlignment('center')
                      }}
                      active={textAlignment.indexOf('center') > -1}
                    >
                      <i className='icon bi-text-center'></i>
                    </Button>
                    <Button
                      variant='secondary'
                      onClick={e => {
                        setTextAlignment('right')
                      }}
                      active={textAlignment.indexOf('right') > -1}
                    >
                      <i className='icon bi-text-right'></i>
                    </Button>
                  </ButtonGroup>
                </Form.Group>
              </Col>
              <Col>
                <Form.Group className='my-3' controlId='formTextShadow'>
                  <small>
                    <label>Shadow</label>
                  </small>
                  <br />
                  <ButtonGroup aria-label='Text Shadow'>
                    <Button
                      variant='secondary'
                      onClick={e => {
                        setTextShadow(0)
                      }}
                      active={!textShadow}
                    >
                      <i className='icon bi-layers'></i>
                    </Button>
                    <Button
                      variant='secondary'
                      onClick={e => {
                        setTextShadow(10)
                      }}
                      active={textShadow}
                    >
                      <i className='icon bi-layers-half'></i>
                    </Button>
                  </ButtonGroup>
                </Form.Group>
              </Col>
            </Row>

            <strong>Background</strong>
            <Row xs={1} s={1} md={2} lg={4}>
              <Col>
                <Form.Group className='my-3' controlId='formBackgroundColor'>
                  <FloatingLabel
                    controlId='floatingBackgroundColor'
                    label='Color'
                  >
                    <Form.Control
                      name='backgroundColor'
                      value={backgroundColor}
                      placeholder='Color'
                      type='color'
                      onChange={e => setBackgroundColor(e.currentTarget.value)}
                    />
                  </FloatingLabel>
                </Form.Group>
              </Col>
              <Col>
                <Form.Group className='my-3' controlId='formBackgroundGradient'>
                  <FloatingLabel
                    controlId='floatingBackgroundGradient'
                    label='Gradient'
                  >
                    <Form.Control
                      name='backgroundGradient'
                      value={backgroundGradient}
                      placeholder='Gradient'
                      type='color'
                      onChange={e =>
                        setBackgroundGradient(e.currentTarget.value)
                      }
                    />
                  </FloatingLabel>
                </Form.Group>
              </Col>
              <Col>
                <Form.Group className='my-3' controlId='formBackground'>
                  <FloatingLabel controlId='floatingBackground' label='Image'>
                    <Form.Select
                      name='background'
                      onChange={e => setBackground(e.currentTarget.value)}
                      value={background}
                    >
                      <option value=''>Select a background</option>
                      {backgrounds.map(option => (
                        <option key={option} value={option}>
                          {option}
                        </option>
                      ))}
                    </Form.Select>
                  </FloatingLabel>
                </Form.Group>
              </Col>
              <Col>
                <Form.Group className='my-3' controlId='formWatermark'>
                  <FloatingLabel
                    controlId='floatingWatermark'
                    label='Watermark'
                  >
                    <Form.Select
                      name='watermark'
                      onChange={e => setWatermark(e.currentTarget.value)}
                      value={watermark}
                    >
                      <option value=''>Select a watermark</option>
                      {watermarks.map(option => (
                        <option key={option} value={option}>
                          {option}
                        </option>
                      ))}
                    </Form.Select>
                  </FloatingLabel>
                </Form.Group>
              </Col>
            </Row>
          </Form>
        </Col>
        <Col>
          <h6>Preview</h6>
          <img className='preview' src={url} alt={text} />
        </Col>
      </Row>
    </Container>
  )
}

export default AffirmationBuilder
